import React from 'react';
import moment from 'moment';
import Tooltip from '@material-ui/core/Tooltip';
import { useTranslation } from 'react-i18next';

function ChildAttendanceCalendar({ visitsInfo, selectedDate, onMonthChange, noData = false, hideNavigation = false }) {
  const startOfMonth = moment(selectedDate).startOf('month');
  const endOfMonth = moment(selectedDate).endOf('month');
  const { t } = useTranslation();


  const currentMonth = moment();
  const prevMonth = moment().subtract(1, 'month');

  const isPrevMonth = selectedDate.isSame(prevMonth, 'month');
  const isCurrentMonth = selectedDate.isSame(currentMonth, 'month');

  const handlePrevMonth = () => {
    if (isCurrentMonth) onMonthChange && onMonthChange(prevMonth.clone());
  };

  const handleNextMonth = () => {
    if (isPrevMonth) onMonthChange && onMonthChange(currentMonth.clone());
  };

  const styles = {
    container: {
      width: '100%',
      maxWidth: 500,
      textAlign: 'center',
      marginTop: 20,
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: 10,
    },
    button: {
      border: 'none',
      background: '#eee',
      padding: '4px 10px',
      borderRadius: '6px',
      cursor: 'pointer',
      fontSize: 16,
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(7, 1fr)',
      gap: 4,
    },
    headerCell: {
      fontWeight: 'bold',
      backgroundColor: '#f2f2f2',
      border: '1px solid #ddd',
      borderRadius: '6px',
      padding: '8px',
    },
    legend: {
      marginTop: 10,
      display: 'flex',
      justifyContent: 'space-around',
    },
    legendBox: {
      display: 'inline-block',
      width: 15,
      height: 15,
      marginRight: 5,
      borderRadius: 3,
    },
  };

  const weekdayKeys = [
    'myReports_weekday_mon',
    'myReports_weekday_tue',
    'myReports_weekday_wed',
    'myReports_weekday_thu',
    'myReports_weekday_fri',
    'myReports_weekday_sat',
    'myReports_weekday_sun',
  ];


  if (noData) {
    const weeks = [];
    let day = startOfMonth.clone().startOf('week');

    while (day.isBefore(endOfMonth.clone().endOf('week'))) {
      const week = [];
      for (let i = 0; i < 7; i++) {
        const inMonth = day.month() === selectedDate.month();
        week.push(
          <div
            key={day.format('YYYY-MM-DD')}
            style={{
              border: '1px solid #ddd',
              borderRadius: '6px',
              padding: '8px',
              minHeight: '50px',
              textAlign: 'center',
              background: inMonth ? '#e9ecef' : '#f9f9f9',
              color: inMonth ? '#999' : '#ccc',
            }}
          >
            <span style={{ fontWeight: 'bold' }}>{day.date()}</span>
          </div>
        );
        day.add(1, 'day');
      }
      weeks.push(week);
    }

    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <button
            style={{ ...styles.button, opacity: !isCurrentMonth ? 0.4 : 1 }}
            onClick={handlePrevMonth}
            disabled={!isCurrentMonth}
          >
            ←
          </button>
          <span>{selectedDate.format('MMMM YYYY')}</span>
          <button
            style={{ ...styles.button, opacity: !isPrevMonth ? 0.4 : 1 }}
            onClick={handleNextMonth}
            disabled={!isPrevMonth}
          >
            →
          </button>
        </div>

        <div style={styles.grid}>
          {weekdayKeys.map(key => (
            <div key={key} style={styles.headerCell}>{t(key)}</div>
          ))}
          {weeks.map((week, i) => (
            <React.Fragment key={i}>{week}</React.Fragment>
          ))}
        </div>

        <div style={{ marginTop: 15, color: '#777' }}>
          <strong>{t('myReports_noData')}</strong>
        </div>
      </div>
    );
  }

  const absenceReasonLabels = {
    LEAVE_APPLICATION: t('myReports_reason_leaveApplication'),
    TECHNICAL_ERROR: t('myReports_reason_technicalError'),
    PARENT_STATEMENT: t('myReports_reason_parentStatement'),
    FAMILY_SITUATION: t('myReports_reason_familySituation'),
    REFERENCE: t('myReports_reason_reference'),
  };

  const visitMap = new Map();
  visitsInfo.forEach(v => {
    const key = moment(v.logDate).format('YYYY-MM-DD');
    visitMap.set(key, v);
  });

  const weeks = [];
  let day = startOfMonth.clone().startOf('week');

  while (day.isBefore(endOfMonth.clone().endOf('week'))) {
    const week = [];
    for (let i = 0; i < 7; i++) {
      const formatted = day.format('YYYY-MM-DD');
      const visit = visitMap.get(formatted);
      const inMonth = day.month() === selectedDate.month();

      let background = '#fafafa';
      let color = '#000';
      let tooltip = '';

      if (inMonth) {
        if (visit) {
          if (visit.isVisited) {
            background = '#d4edda'; // зелёный
          } else if (visit.absenceReason) {
            background = '#fff3cd'; // жёлтый
            tooltip = absenceReasonLabels[visit.absenceReason] || 'Причина отсутствия';
          } else {
            background = '#f8d7da'; // красный
          }
        } else {
          background = '#f0f0f0';
          color = '#aaa';
        }
      } else {
        background = '#f9f9f9';
        color = '#ccc';
      }

      const cell = (
        <div
          key={formatted}
          style={{
            border: '1px solid #ddd',
            borderRadius: '6px',
            padding: '8px',
            minHeight: '50px',
            textAlign: 'center',
            background,
            color,
            cursor: tooltip ? 'help' : 'default',
          }}
        >
          <span style={{ fontWeight: 'bold' }}>{day.date()}</span>
        </div>
      );

      week.push(
        tooltip ? (
          <Tooltip key={formatted} title={tooltip} arrow placement="top">
            {cell}
          </Tooltip>
        ) : (
          cell
        )
      );

      day.add(1, 'day');
    }
    weeks.push(week);
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <button
          style={{ ...styles.button, opacity: !isCurrentMonth ? 0.4 : 1 }}
          onClick={hideNavigation ? undefined : handlePrevMonth}
          disabled={hideNavigation || !isCurrentMonth}
        >
          ←
        </button>
        <span>{selectedDate.format('MMMM YYYY')}</span>
        <button
          style={{ ...styles.button,
            opacity: hideNavigation ? 0.4 : !isPrevMonth ? 0.4 : 1,}}
          onClick={hideNavigation ? undefined : handleNextMonth}
          disabled={hideNavigation || !isPrevMonth}
        >
          →
        </button>
      </div>

      <div style={styles.grid}>
        {weekdayKeys.map(key => (
          <div key={key} style={styles.headerCell}>{t(key)}</div>
        ))}
        {weeks.map((week, i) => (
          <React.Fragment key={i}>{week}</React.Fragment>
        ))}
      </div>

      <div style={styles.legend}>
        <div>
          <span style={{ ...styles.legendBox, backgroundColor: '#28a745' }}></span>
          {t('myReports_attempted')}
        </div>
        <div>
          <span style={{ ...styles.legendBox, backgroundColor: '#fff3cd', border: '1px solid #ccc' }}></span>
          {t('myReports_absenceWithReason')}
        </div>
        <div>
          <span style={{ ...styles.legendBox, backgroundColor: '#dc3545' }}></span>
          {t('myReports_absenceWithoutReason')}
        </div>
      </div>
    </div>
  );
}

export default ChildAttendanceCalendar;
